import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Spinner, Table } from 'reactstrap';
import { api } from '../api/client.js';
import { WeekSelector } from '../components/WeekSelector.jsx';
import { useCurrentWeek } from '../hooks/useCurrentWeek.js';

export function WeeklyLeaderboard() {
  const current = useCurrentWeek();
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (current.season && season === null) {
      setSeason(current.season);
      setWeek(current.week);
    }
  }, [current, season]);

  const load = useCallback(async () => {
    if (!season || !week) return;
    setLoading(true);
    setError(null);

    try {
      setBoard(await api.weeklyLeaderboard(season, week));
    } catch (err) {
      setError(err.message);
      setBoard(null);
    } finally {
      setLoading(false);
    }
  }, [season, week]);

  useEffect(() => { load(); }, [load]);

  function pickCell(entry, game) {
    const pick = entry.picks.find((p) => p.gameId === game.id);
    if (!pick) return <span className="text-muted">&mdash;</span>;

    const team = pick.selectedTeamId === game.home.id ? game.home : game.away;
    const colour = pick.isCorrect === true ? 'text-success fw-semibold'
      : pick.isCorrect === false ? 'text-danger text-decoration-line-through'
        : '';

    return <span className={colour}>{team.abbreviation}</span>;
  }

  return (
    <div>
      <h1 className="h3 mb-3">Weekly leaderboard</h1>

      <WeekSelector season={season} week={week} disabled={loading}
        onChange={(s, w) => { setSeason(s); setWeek(w); }} />

      {error && <Alert fade={false} color="danger">{error}</Alert>}
      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && board && (
        <>
          {!board.picksRevealed && (
            <Alert fade={false} color="info" className="small">
              Everyone&apos;s picks stay hidden until the first game of the week kicks off.
            </Alert>
          )}

          {board.tiebreakerActualTotal != null && (
            <p className="text-muted small">
              Tiebreaker game finished with a combined score of{' '}
              <strong>{board.tiebreakerActualTotal}</strong>.
            </p>
          )}

          {board.entries.length === 0 && <Alert fade={false} color="secondary">Nobody has made picks for this week yet.</Alert>}

          {board.entries.length > 0 && (
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th className="text-center">Correct</th>
                    {board.picksRevealed && board.games.map((g) => (
                      <th key={g.id} className="text-center small text-nowrap">
                        {g.away.abbreviation}@{g.home.abbreviation}
                      </th>
                    ))}
                    <th className="text-center">Tiebreak</th>
                  </tr>
                </thead>
                <tbody>
                  {board.entries.map((entry) => (
                    <tr key={entry.userId}>
                      <td>{entry.rank}</td>
                      <td className="text-nowrap">
                        {entry.displayName}{' '}
                        {entry.isWeekWinner && <Badge color="warning" className="text-dark" pill>Week won</Badge>}
                      </td>
                      <td className="text-center fw-semibold">
                        {entry.correctPicks}/{entry.gamesScored}
                      </td>
                      {board.picksRevealed && board.games.map((g) => (
                        <td key={g.id} className="text-center small">{pickCell(entry, g)}</td>
                      ))}
                      <td className="text-center small">
                        {entry.tiebreakerTotalPoints ?? '\u2014'}
                        {entry.tiebreakerDelta != null && (
                          <span className="text-muted"> ({entry.tiebreakerDelta > 0 ? '\u00b1' : ''}{entry.tiebreakerDelta})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
